import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react';
import VoxelDialog from '../../../../components/voxel/Dialogs/VoxelDialog';
import VoxelDialogActionCancel from '../../../../components/voxel/Dialogs/VoxelDialogActionCancel';
import VoxelDialogActionSubmit from '../../../../components/voxel/Dialogs/VoxelDialogActionSubmit';
import VoxelDialogActions from '../../../../components/voxel/Dialogs/VoxelDialogActions';
import VoxelDialogContent from '../../../../components/voxel/Dialogs/VoxelDialogContent';
import VoxelTable from '../../../../components/voxel/Tables/VoxelTable';
import VoxelTableCell from '../../../../components/voxel/Tables/VoxelTableCell';
import VoxelTableHeadCell from '../../../../components/voxel/Tables/VoxelTableHeadCell';
import VoxelTableRow from '../../../../components/voxel/Tables/VoxelTableRow';
import VoxelText from '../../../../components/voxel/Text/VoxelText';
import { ContentMissingKeywordGq } from '../../../../graphql/projects/models/ContentMissingKeywordGq';
import { DomainTypeGq } from '../../../../graphql/projects/models/DomainTypeGq';
import { ProjectGq } from '../../../../graphql/projects/models/ProjectGq';
import { projectsApiService } from '../../../../graphql/projects/projectsApiService';

export interface ContentPlanAddMissingDialogProps {
  openDialog: boolean;
  setOpenDialog: Dispatch<SetStateAction<boolean>>;
  setProgressOpen: (open: boolean) => void;
  missingKeywordCount: number;
  missingKeywords: ContentMissingKeywordGq[];
  reload: () => Promise<any>;
  uploadId?: number;
  tagPlanId?: number;
  project: ProjectGq;
}

const ContentPlanAddMissingDialog: React.FC<ContentPlanAddMissingDialogProps> = ({
  openDialog,
  setOpenDialog,
  missingKeywordCount,
  missingKeywords,
  setProgressOpen,
  uploadId,
  tagPlanId,
  project,
  reload,
}) => {
  // Automatic open dialog
  const alreadyViewedKey = `missing-reminder-${project.id}-${project.serpAutomaticDomainComputed}`;
  useEffect(() => {
    if (!missingKeywordCount) return;
    const alreadyViewed = sessionStorage.getItem(alreadyViewedKey);
    if (alreadyViewed) return;
    setOpenDialog(true);
  }, [missingKeywordCount, project.serpAutomaticDomainComputed]);

  //Close dialog
  const closeDialog = useCallback(() => {
    setOpenDialog(false);
    sessionStorage.setItem(alreadyViewedKey, 'true');
  }, []);

  const clientDomainId = project?.domains?.find((d) => d.type === DomainTypeGq.PROJECT)?.id;

  // Mutation
  const initiateScrapeRerunMutation = projectsApiService.mutations.contentPlan.useInitiateRerun();
  const initiateTagRerunMutation =
    projectsApiService.mutations.contentPlan.useInitiateTagBasedPlan();
  const [updating, setUpdating] = useState(false);
  const handleInitiateRerun = () => {
    setUpdating(true);
    const mutationPromise = tagPlanId
      ? initiateTagRerunMutation({ rerunPlanId: tagPlanId, projectId: project.id })
      : initiateScrapeRerunMutation({ uploadId, projectId: project.id });
    mutationPromise.then(reload).finally(() => {
      setUpdating(false);
      setProgressOpen(true);
      closeDialog();
    });
  };

  return (
    <VoxelDialog
      maxWClass="max-w-4xl"
      open={openDialog}
      onClose={closeDialog}
      title="Add missing keywords"
      subtitle={`You have ${missingKeywordCount} unarchived keywords with rank data that are missing from the content plan. Do you wish to add them?`}
    >
      <VoxelDialogContent>
        <div className="my-3 max-h-[600px] overflow-y-auto">
          <VoxelTable>
            <VoxelTableRow>
              <VoxelTableHeadCell size='md'>Keyword</VoxelTableHeadCell>
              <VoxelTableHeadCell size='md' align="right">
                Volume
              </VoxelTableHeadCell>
              <VoxelTableHeadCell size='md' align="right">
                Rank
              </VoxelTableHeadCell>
            </VoxelTableRow>
            <tbody>
              {missingKeywords.map((keyword) => (
                <VoxelTableRow key={keyword.id}>
                  <VoxelTableCell size='md'>{keyword.keyword}</VoxelTableCell>
                  <VoxelTableCell size='md' align="right">
                    {keyword.volume}
                  </VoxelTableCell>
                  <VoxelTableCell size='md' align="right">
                    {keyword.domainRanks?.find((dr) => dr?.domainId === clientDomainId)
                      ? keyword.domainRanks?.find((dr) => dr?.domainId === clientDomainId)?.rank
                      : '100+'}
                  </VoxelTableCell>
                </VoxelTableRow>
              ))}
            </tbody>
          </VoxelTable>
        </div>
        {missingKeywordCount > 20 && (
          <div className="flex flex-row justify-end">
            <VoxelText align="right" variant="title3">
              ...and {missingKeywordCount - missingKeywords.length} more
            </VoxelText>
          </div>
        )}
      </VoxelDialogContent>

      <VoxelDialogActions>
        <VoxelDialogActionCancel onClick={closeDialog} />
        <VoxelDialogActionSubmit title={'Add missing keywords'} onClick={handleInitiateRerun} />
      </VoxelDialogActions>
    </VoxelDialog>
  );
};

export default ContentPlanAddMissingDialog;
